import React from 'react';
import { CalendarCheck2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * DashboardHero — premium greeting hero for the vendor and customer
 * dashboard "welcome" tab.
 *
 * Visual upgrade (Feb 2026): the dashboard previously opened with a
 * plain `<p>` greeting on the warm page background, with no focal
 * anchor. This hero lifts the greeting into a tinted emerald card with
 * subtle gradient, a soft decorative calendar mark, and a faint sparkle
 * accent — matching the concept reference without introducing any
 * illustration assets, animation systems, or CTAs.
 *
 * Strictly presentational. No new fetches, no new state, no new routes.
 * The greeting text + subtitle are exactly the values the previous
 * inline block computed.
 *
 * Props:
 *   greeting  — "Good morning, Jane" / "Good evening, Test DJ Co" etc.
 *   subtitle  — e.g. "Here's what needs your attention"
 *   testid    — preserved data-testid (default: 'dashboard-header-section')
 */
export const DashboardHero = ({ greeting, subtitle, testid = 'dashboard-header-section', children }) => {
  return (
    <div
      data-testid={testid}
      className={cn(
        'relative overflow-hidden rounded-2xl',
        'bg-gradient-to-br from-emerald-50 via-emerald-50/60 to-white',
        'border border-emerald-100/70 shadow-card',
        'px-5 py-5 sm:px-6 sm:py-6'
      )}
    >
      {/* Decorative calendar mark — kept low-opacity so it reads as
          ambient texture, not illustration. */}
      <CalendarCheck2
        className="absolute -right-3 -bottom-3 h-24 w-24 text-emerald-100/80"
        aria-hidden="true"
      />
      <Sparkles
        className="absolute right-5 top-4 h-4 w-4 text-emerald-300/80"
        aria-hidden="true"
      />

      <div className="relative">
        <h1
          className="text-[22px] sm:text-[24px] font-semibold text-gray-900 leading-tight tracking-tight"
          data-testid="dashboard-greeting"
        >
          {greeting}
        </h1>
        {subtitle && (
          <p className="mt-1 text-[13px] text-gray-600">
            {subtitle}
          </p>
        )}
        {children && (
          <div className="mt-4">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHero;
